/* eslint-disable @typescript-eslint/no-unused-vars */
import { IStore } from "store/store-context";
import {
  CurrencyType,
  DateRangeValue,
  DocumentAnswer,
  DocumentBase,
  DocumentMeeting,
  DocumentQuestion,
  DocumentSection,
  DocumentSectionMeetingNotes,
  ICreateDocumentAIAnswerPayload,
  ICreateDocumentData,
  ICreateDocumentQuestion,
  ICreateDocumentSectionData,
  ICreateGlobalDocumentShareData,
  ICreateMeetingSectionData,
  IGetGlobalDocumentsQuery,
  IGetGlobalDocumentsResponse,
  IGetSharesData,
  IMoveDocumentData,
  IUpdateDocumentQuestionData,
  IUpdateDocumentSectionData,
  IUpdateGlobalDocumentShareData,
  IUpdateMeetingSectionData,
  IUploadGlobalDocumentFileData,
  ReportFilters,
  ShareDocumentSectionsData,
  UpdateDocumentAnswerData
} from "store/types";
import { AltiusRes, IFile, IFolder, Optional } from "store/types";
import { Document } from "store/types";
import { toFormData } from "utils";
import downloadFileAxios from "utils/download-file-axios";
import { toastify } from "utils/tyostify";

import { DealAction, DocumentsAction } from "../actions";
import { AsyncActions, AsyncActionsParams } from "../base";

export interface IDocumentsActions {
  getDocumentsAction: (dealId: number) => Promise<void>;
  createDocumentAction: (data: ICreateDocumentData) => Promise<Document>;
  getDocumentContentAction: (id: number) => Promise<Document<true>>;
  getGlobalDocumentsAction: (params: IGetGlobalDocumentsQuery) => Promise<IGetGlobalDocumentsResponse>;
  createGlobalDocumentAction: (title: string) => Promise<Document>;
  invalidateDocumentsAction: () => void;
  updateGlobalDocumentAction: (
    data: Partial<Pick<DocumentBase, "title" | "is_private">> & Pick<DocumentBase, "id">
  ) => Promise<Document<false>>;
  updateDocumentAction: (
    data: Partial<Pick<Document<true>, "title" | "is_private" | "report_data" | "section_id">> &
      Pick<Document, "id"> & { make_global?: boolean }
  ) => Promise<Document<false>>;
  deleteDocumentAction: (id: number) => Promise<void>;

  uploadGlobalDocumentFileAction: (data: IUploadGlobalDocumentFileData) => Promise<IFile>;

  createDocumentSectionAction: (data: ICreateDocumentSectionData) => Promise<DocumentSection>;
  updateDocumentSectionAction: (data: IUpdateDocumentSectionData) => Promise<DocumentSection>;
  deleteDocumentSectionAction: (sectionId: number) => Promise<void>;

  createDocumentQuestionAction: (data: ICreateDocumentQuestion) => Promise<DocumentQuestion>;
  updateDocumentQuestionAction: (data: IUpdateDocumentQuestionData) => Promise<DocumentQuestion>;
  deleteDocumentQuestionAction: (questionId: number) => Promise<void>;

  updateDocumentAnswerAction: (data: UpdateDocumentAnswerData) => Promise<DocumentAnswer>;
  deleteDocumentAnswerAction: (answerId: number) => Promise<void>;
  createDocumentAiAnswerAction: (question_id: number) => Promise<void>;

  createDocumentAnswerAction: (
    data: Optional<Pick<DocumentAnswer, "question_id" | "answer" | "parent_answer_id">, "parent_answer_id"> & {
      documentId: number;
    }
  ) => Promise<DocumentAnswer>;

  reorderDocumentSectionsAction: (data: {
    order: { id: number; position: number }[];
    documentId: number;
  }) => Promise<void>;
  shareDocumentSectionAction: (data: ShareDocumentSectionsData) => Promise<DocumentSection[]>;
  getDealFoldersAction: (data: { dealId: number; kind?: IFolder["kind"] }) => Promise<Record<number, IFolder>>;
  getDealFilesAction: (dealId: number) => Promise<Record<number, IFile>>;
  moveDocumentAction: (data: IMoveDocumentData) => Promise<Document>;

  downloadDocumentFileAction: (data: { documentId: number; fileId: number }) => Promise<void>;
  generateAiDocumentAnswerAction: (
    question: string,
    documentId: number
  ) => Promise<{ question: string; answer: string }>;
  generateAiDocumentQuestionAnswersAction: (data: {
    question_id: number;
    documentId: number;
  }) => Promise<{ question: DocumentQuestion; answer: DocumentAnswer }>;
  generateDocumentsAiQuestionAnswersAction: (data: ICreateDocumentAIAnswerPayload) => Promise<DocumentSection>;
  getDocumentSectionDataAction: (
    sectionId: number
  ) => Promise<{ section: DocumentSection; answers: DocumentAnswer[]; questions: DocumentQuestion[] }>;

  createGlobalDocumentShareAction: (data: ICreateGlobalDocumentShareData) => Promise<void>;
  getGlobalDocumentShareAction: (id: number) => Promise<IGetSharesData[]>;
  updateGlobalDocumentShareAction: (data: IUpdateGlobalDocumentShareData) => Promise<void>;

  // --------------  meetings ------------
  createMeetingSectionAction: (body: ICreateMeetingSectionData) => Promise<DocumentSectionMeetingNotes>;
  getMeetingsAction: () => Promise<DocumentMeeting[]>;
  updateMeetingSectionAction: (data: {
    meetingId: number;
    body: IUpdateMeetingSectionData;
  }) => Promise<DocumentSectionMeetingNotes>;
  moveMeetingSectionAction: (body: {
    document_id: number;
    meeting_id: number;
    document_section_order: number;
  }) => Promise<DocumentSectionMeetingNotes>;

  getDealMeetingsAction: (dealId: number) => Promise<DocumentMeeting[]>;
}

interface DocumentsActionParams extends AsyncActionsParams {
  isSharedDeal: boolean;
  dealId: number | null;
}

export class DocumentsActions extends AsyncActions implements IDocumentsActions {
  protected readonly isSharedDeal: boolean;
  protected readonly dealId: number | null;
  constructor({ isSharedDeal, dealId, ...params }: DocumentsActionParams) {
    super(params);
    this.isSharedDeal = isSharedDeal;
    this.dealId = dealId;
  }

  public getDocumentsAction: IDocumentsActions["getDocumentsAction"] = async (deal_id) => {
    const {
      data: { data }
    } = await this.api.get<AltiusRes<IStore["documents"]>>({
      url: this.isSharedDeal ? `/api/v0.0.3/shared-deals/${this.dealId}/documents` : "/api/v0.0.3/documents",
      params: { deal_id }
    });
    // this.dispatch({ type: DocumentsAction.GetDocumentsSuccess, payload: data });
  };

  public createDocumentAction: IDocumentsActions["createDocumentAction"] = async ({ disableDispatch, ...body }) => {
    const {
      data: { data: payload }
    } = await this.api.post<AltiusRes<Document>>({
      url:
        this.isSharedDeal && this.dealId
          ? `/api/v0.0.3/shared-deals/${this.dealId}/documents`
          : "/api/v0.0.3/documents",
      data: body
    });
    if (!disableDispatch) {
      this.dispatch({ type: DocumentsAction.CreateDocumentSuccess, payload });
    }
    return payload;
  };

  public getDocumentContentAction: IDocumentsActions["getDocumentContentAction"] = async (id) => {
    const {
      data: { data }
    } = await this.api.get<AltiusRes<Document<true>>>({
      url:
        this.isSharedDeal && this.dealId
          ? `/api/v0.0.3/shared-deals/${this.dealId}/documents/${id}`
          : `/api/v0.0.3/documents/${id}`,
      signal: this.addProcess("getDocumentContentAction").signal
    });

    this.removeProcess("getDocumentContentAction");
    // this.dispatch({ type: DocumentsAction.GetDocumentContentSuccess, payload: data });
    return data;
  };

  public getGlobalDocumentsAction: IDocumentsActions["getGlobalDocumentsAction"] = async (params) => {
    const { replaceDocuments, ...rest } = params;
    const {
      data: { data }
    } = await this.api.get<AltiusRes<IGetGlobalDocumentsResponse>>({
      url: "/api/v0.0.3/global-documents",
      params: rest
    });

    const actionType = replaceDocuments
      ? DocumentsAction.GetDocumentsSuccess
      : DocumentsAction.GetPaginatedDocumentsSuccess;

    this.dispatch({
      type: actionType,
      payload: data.documents
    });
    return data;
  };

  public createGlobalDocumentAction: IDocumentsActions["createGlobalDocumentAction"] = async (title) => {
    const {
      data: { data: document }
    } = await this.api.post<AltiusRes<Document>>({
      url: "api/v0.0.3/global-documents",
      data: {
        title
      }
    });
    this.dispatch({ type: DocumentsAction.CreateDocumentSuccess, payload: document });
    return document;
  };

  public invalidateDocumentsAction: IDocumentsActions["invalidateDocumentsAction"] = () => {
    this.dispatch({ type: DocumentsAction.InvalidateDocuments, payload: null });
  };

  public updateGlobalDocumentAction: IDocumentsActions["updateGlobalDocumentAction"] = async ({ id, ...data }) => {
    const {
      data: { data: document }
    } = await this.api.patch<AltiusRes<Document>>({
      url: `api/v0.0.3/global-documents/${id}`,
      data
    });
    this.dispatch({ type: DocumentsAction.UpdateDocumentSuccess, payload: document });
    return document;
  };

  public updateDocumentAction: IDocumentsActions["updateDocumentAction"] = async ({ id, ...data }) => {
    const {
      data: { data: payload }
    } = await this.api.patch<AltiusRes<Document<false>>>({ url: `/api/v0.0.3/documents/${id}`, data });
    this.dispatch({ type: DocumentsAction.UpdateDocumentSuccess, payload });
    return payload;
  };

  public deleteDocumentAction: IDocumentsActions["deleteDocumentAction"] = async (id) => {
    await this.api.delete({ url: `/api/v0.0.3/documents/${id}` });
    this.dispatch({ type: DocumentsAction.DeleteDocumentSuccess, payload: id });
  };

  public uploadGlobalDocumentFileAction: IDocumentsActions["uploadGlobalDocumentFileAction"] = async (data) => {
    const { file, ...rest } = data;
    const {
      data: { data: fileFromAPi }
    } = await this.api.post<AltiusRes<IFile>>({
      url: "api/v0.0.3/global-documents/files",
      data: toFormData({ ...rest, file }),
      headers: {
        "Content-Type": "multipart/form-data"
      }
    });
    return fileFromAPi;
  };

  public createDocumentSectionAction: IDocumentsActions["createDocumentSectionAction"] = async (body) => {
    const {
      data: { data }
    } = await this.api.post<AltiusRes<DocumentSection>>({
      url: "/api/v0.0.3/documents/sections",
      data: body
    });
    return data;
  };

  public updateDocumentSectionAction: IDocumentsActions["updateDocumentSectionAction"] = async ({ id, ...body }) => {
    const {
      data: { data }
    } = await this.api.patch<AltiusRes<DocumentSection>>({
      url: `/api/v0.0.3/documents/sections/${id}`,
      data: body
    });
    // this.dispatch({ type: DocumentsAction.UpdateDocumentSection, payload: data });
    return data;
  };

  public deleteDocumentSectionAction: IDocumentsActions["deleteDocumentSectionAction"] = async (sectionId) => {
    await this.api.delete({ url: `/api/v0.0.3/documents/sections/${sectionId}` });
  };

  public createDocumentQuestionAction: IDocumentsActions["createDocumentQuestionAction"] = async ({
    documentId,
    ...body
  }) => {
    const {
      data: { data }
    } = await this.api.post<AltiusRes<DocumentQuestion>>({ url: "/api/v0.0.3/documents/questions", data: body });
    return data;
  };

  public updateDocumentQuestionAction: IDocumentsActions["updateDocumentQuestionAction"] = async ({ id, ...body }) => {
    const {
      data: { data: payload }
    } = await this.api.patch<AltiusRes<DocumentQuestion>>({
      url: `/api/v0.0.3/documents/questions/${id}`,
      data: body
    });
    return payload;
  };

  public deleteDocumentQuestionAction: IDocumentsActions["deleteDocumentQuestionAction"] = async (questionId) => {
    await this.api.delete({ url: `/api/v0.0.3/documents/questions/${questionId}` });
  };

  public updateDocumentAnswerAction: IDocumentsActions["updateDocumentAnswerAction"] = async ({
    id,
    documentId,
    ...body
  }) => {
    const {
      data: { data }
    } = await this.api.patch<AltiusRes<DocumentAnswer>>({
      url:
        this.isSharedDeal && this.dealId
          ? `/api/v0.0.3/shared-deals/${this.dealId}/documents/answers/${id}`
          : `/api/v0.0.3/documents/answers/${id}`,
      data: body
    });
    return data;
  };

  public deleteDocumentAnswerAction: IDocumentsActions["deleteDocumentAnswerAction"] = async (answerId) => {
    // delete_draft: true - make the last answer from the history the current answer
    await this.api.delete({ url: `/api/v0.0.3/documents/answers/${answerId}`, data: { delete_draft: false } });
  };

  public createDocumentAiAnswerAction: IDocumentsActions["createDocumentAiAnswerAction"] = async (question_id) => {
    await this.api.post<AltiusRes<DocumentAnswer>>({
      url: "/api/v0.0.3/documents/ai-answer",
      data: { question_id }
    });
  };

  public createDocumentAnswerAction: IDocumentsActions["createDocumentAnswerAction"] = async ({
    documentId,
    ...body
  }) => {
    const {
      data: { data }
    } = await this.api.post<AltiusRes<DocumentAnswer>>({
      url:
        this.isSharedDeal && this.dealId
          ? `/api/v0.0.3/shared-deals/${this.dealId}/documents/answers`
          : "/api/v0.0.3/documents/answers",
      data: { ...body }
    });
    // this.dispatch({ type: DocumentsAction.AddDocumentAnswer, payload: { ...data, documentId } });
    return data;
  };

  public reorderDocumentSectionsAction: IDocumentsActions["reorderDocumentSectionsAction"] = async ({
    documentId,
    order
  }) => {
    // this.dispatch({ type: DocumentsAction.ReorderDocumentSections, payload: { documentId, sections: order } });
    await this.api.patch({
      url: `/api/v0.0.3/documents/${documentId}/sections/reorder`,
      data: { order }
    });
  };

  public shareDocumentSectionAction: IDocumentsActions["shareDocumentSectionAction"] = async ({
    documentId,
    ...body
  }) => {
    const {
      data: { data }
    } = await this.api.post<AltiusRes<DocumentSection[]>>({
      url: `/api/v0.0.3/documents/${documentId}/share`,
      data: body
    });
    // this.dispatch({ type: DocumentsAction.UpdateDocumentSections, payload: { sections: data, documentId } });
    return data;
  };

  public getDealFoldersAction: IDocumentsActions["getDealFoldersAction"] = async ({ dealId, kind }) => {
    const {
      data: { data: payload }
    } = await this.api.get<AltiusRes<Record<number, IFolder>>>({
      url: `/api/v0.0.3/deals/${dealId}/folders`,
      ...(kind && { params: { kind } })
    });
    return payload;
  };

  public getDealFilesAction: IDocumentsActions["getDealFilesAction"] = async (dealId: number) => {
    const {
      data: { data: payload }
    } = await this.api.get<AltiusRes<Record<number, IFile>>>({
      url: `/api/v0.0.3/deals/${dealId}/files`,
      params: {
        type: "pdf"
      }
    });
    return payload;
  };

  public moveDocumentAction: IDocumentsActions["moveDocumentAction"] = async (data) => {
    const { documentId, ...rest } = data;
    const {
      data: { data: document }
    } = await this.api.post<AltiusRes<Document>>({
      url: `api/v0.0.3/global-documents/${documentId}/move`,
      data: rest
    });
    return document;
  };

  public downloadDocumentFileAction: IDocumentsActions["downloadDocumentFileAction"] = async ({
    documentId,
    fileId
  }) => {
    return this.api
      .get({
        url: `/api/v0.0.3/global-documents/${documentId}/files/${fileId}/save`,
        responseType: "blob"
      })
      .then(downloadFileAxios);
  };

  public generateAiDocumentAnswerAction: IDocumentsActions["generateAiDocumentAnswerAction"] = async (
    question: string,
    documentId: number
  ) => {
    const {
      data: { data }
    } = await this.api.post<AltiusRes<{ question: string; answer: string }>>({
      url: `/api/v0.0.3/documents/${documentId}/ask-ai`,
      data: {
        question
      }
    });
    return data;
  };

  public generateAiDocumentQuestionAnswersAction: IDocumentsActions["generateAiDocumentQuestionAnswersAction"] =
    async ({ question_id, documentId }) => {
      const {
        data: { data }
      } = await this.api.post<AltiusRes<{ question: DocumentQuestion; answer: DocumentAnswer }>>({
        url: `/api/v0.0.3/documents/${documentId}/ask-ai`,
        data: { question_id }
      });
      return data;
    };

  public generateDocumentsAiQuestionAnswersAction: IDocumentsActions["generateDocumentsAiQuestionAnswersAction"] =
    async (data) => {
      const { file_ids, files, sectionId, query_type, user_prompt } = data;
      const updatedData = { file_ids, files, query_type, user_prompt };
      const res = Object.fromEntries(Object.entries(updatedData).filter(([, value]) => value?.length));
      const {
        data: { data: dataFromApi }
      } = await this.api.post<AltiusRes<DocumentSection>>({
        url: `/api/v0.0.3/documents/sections/${sectionId}/ai-answer`,
        data: toFormData({
          ...res,
          send_ai: Number(data.send_ai)
        }),
        headers: {
          "Content-Type": "multipart/form-data"
        }
      });
      // this.dispatch({ type: DocumentsAction.UpdateDocumentSection, payload: dataFromApi });
      return dataFromApi;
    };

  public getDocumentSectionDataAction: IDocumentsActions["getDocumentSectionDataAction"] = async (
    sectionId: number
  ) => {
    const {
      data: { data }
    } = await this.api.get<AltiusRes<DocumentSection & { answers: DocumentAnswer[]; questions: DocumentQuestion[] }>>({
      url: `/api/v0.0.3/documents/sections/${sectionId}`
    });
    const { answers, questions, ...rest } = data;
    const newData = { section: rest, answers, questions };
    // this.dispatch({ type: DocumentsAction.UpdateDocumentSectionData, payload: newData });
    return newData;
  };

  public createGlobalDocumentShareAction: IDocumentsActions["createGlobalDocumentShareAction"] = async (data) => {
    await this.api.post({ url: "/api/v0.0.3/global-documents/share", data });
    toastify({ message: `Invitation to collaborate was sent via email to <b> ${data.emails.join(" , ")}</b>` });
  };

  public getGlobalDocumentShareAction: IDocumentsActions["getGlobalDocumentShareAction"] = async (id) => {
    const {
      data: { data: payload }
    } = await this.api.get<AltiusRes<IGetSharesData[]>>({
      url: `/api/v0.0.3/global-documents/${id}/share-data`
    });
    return payload;
  };

  public updateGlobalDocumentShareAction: IDocumentsActions["updateGlobalDocumentShareAction"] = async (data) => {
    const { document_id, ...rest } = data;
    await this.api.patch({ url: `/api/v0.0.3/global-documents/${document_id}/share-data`, data: rest });
  };

  // --------------  meetings ------------
  public createMeetingSectionAction: IDocumentsActions["createMeetingSectionAction"] = async (body) => {
    const {
      data: { data }
    } = await this.api.post<AltiusRes<DocumentSectionMeetingNotes>>({
      url: "/api/v0.0.3/meetings",
      data: body
    });

    // this.dispatch({ type: DocumentsAction.AddDocumentSection, payload: data });
    return data;
  };

  public getMeetingsAction: IDocumentsActions["getMeetingsAction"] = async () => {
    const {
      data: { data }
    } = await this.api.get<AltiusRes<DocumentMeeting[]>>({
      url: "/api/v0.0.3/meetings"
    });
    return data;
  };

  public getDealMeetingsAction: IDocumentsActions["getDealMeetingsAction"] = async (dealId) => {
    const {
      data: { data }
    } = await this.api.get<AltiusRes<DocumentMeeting[]>>({
      url: "/api/v0.0.3/meetings",
      params: {
        deal_id: dealId
      }
    });
    return data;
  };

  public updateMeetingSectionAction: IDocumentsActions["updateMeetingSectionAction"] = async ({ meetingId, body }) => {
    const {
      data: { data }
    } = await this.api.patch<AltiusRes<DocumentSectionMeetingNotes>>({
      url: `/api/v0.0.3/meetings/${meetingId}`,
      data: body
    });
    // this.dispatch({ type: DocumentsAction.UpdateDocumentSection, payload: data });
    return data;
  };

  public moveMeetingSectionAction: IDocumentsActions["moveMeetingSectionAction"] = async (body) => {
    const { meeting_id, ...rest } = body;
    const {
      data: { data }
    } = await this.api.post<AltiusRes<DocumentSectionMeetingNotes>>({
      url: `/api/v0.0.3/meetings/${meeting_id}/move`,
      data: rest
    });
    // this.dispatch({ type: DocumentsAction.AddDocumentSection, payload: data });
    return data;
  };
}
